/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Header } from "../common/header";
import { getIndustryRoles } from "../../services/generalInfo";
import { TGenericGUIDName, TProcessStates } from "../../lib/types/general";
import { useTechnicians } from "../../services/technicians";
import { InviteButton } from "../common/invite-button";
import { TApiTechnician, TEducation, TExperience } from "../../lib/types/dentalTechs";
import { NotificationProvider, useNotificationContext } from "../../contexts/notification-context";
import { useAuthContext } from "../../contexts/auth-context";
import { getCacheKillQueryString } from "../../lib/common/api";
import { FileUploaderComponent, useFileManagement } from "../../services/file-management";
import { BackButton } from "../common/backbutton";
import { ResumeViewer } from "../common/resume-viewer";
import { useUserService } from "../../services/users";
import { PositionsCheckListNoFormik } from "../common/positions-check-list";
import { PillInput, usePillInput } from "../common/pillInput";
import { ConnectionRequestInfoAndButtons } from "../common/notifications";
import { getRegions } from "../../services/regions";
import { Footer } from "./main-page/footer";
import { DEVICES_SIZES_IN_PX, ERROR_IS_FRAUD, MEDIUM_TEXT_INPUT_MAX_CHARACTERS, LARGE_TEXT_INPUT_MAX_CHARACTERS } from "../../lib/common/constants";
import { ReportButton } from "../common/report-button";
import { Spinner } from "../common/spinner";
import { UserNotExist } from "../common/user-not-exist";
import { ErrorMessageFraud, ErrorMessageSticky } from "../common/alert-message";
import { isNumberFromString } from "../../lib/common/validation";
import { WelcomeModal } from "../common/welcomeModal";
import { debounce } from "../../lib/utils";
import { MessageBtn } from "../common/message-button";
import { IncognitoButton } from "../common/incognitobutton";
import { RegionsCheckListNoFormik } from "../common/regions-check-list";
import { TEmployeeRole } from "../../lib/types/employee_roles";
import { GallerySection } from "./dental-lab-info/gallery-section";
import { LogoTech } from "../logo";
import { evaluators, useWindowSizeMatch } from "../../lib/window-size-matcher";
import { OnboardingProcess } from "../common/onboarding-process";
import { DentalTechInfoContactEdit } from "./dental-tech-info-contact-edit";
import { DentalTechInfoContactEditable, DentalTechInfoContactReadonly } from "./dental-tech-info-contact-editable";
import { useOnboardingProcess } from "../../lib/common/onboarding_steps";
import { ProfileDock } from "../common/profile-dock";

declare global {
    interface Window {
        wwgTechInfo_dataWasModified: boolean;
    }
}

type TDentalTechProps = {
    editable?: boolean;
    userExist: boolean;
    techInfo?: TApiTechnician;
};

type TSalaryFrequency = "hourly" | "yearly";
const currencyFormatter = new Intl.NumberFormat();

const WorkEvidence = ({
    setIsSomePartBeingEdited,
    userId,
    setUploadErrorMessage,
    editable,
    isSomePartBeingEdited,
    currentUserInfo,
    onboardingStepCompleted,
    isIncognito,
}: any) => {
    const isCandidate = currentUserInfo.type === "tech";
    const title = `PORTFOLIO GALLERY${isCandidate ? " *" : ""}`;
    const titleIcon = "fa-images";
    const placeholder = "Click on Edit to upload photos of your work, skills in action, certifications, or achievements.";
    return (
        <GallerySection
            setIsSomePartBeingEdited={setIsSomePartBeingEdited}
            userId={userId}
            setUploadErrorMessage={setUploadErrorMessage}
            editable={editable}
            isSomePartBeingEdited={isSomePartBeingEdited}
            title={title}
            titleIcon={titleIcon}
            placeholder={placeholder}
            onboardingStepCompleted={onboardingStepCompleted}
            isIncognito={isIncognito}
        />
    );
};

export const TechInfo = ({ editable = false, techInfo, userExist: paramUserExist }: TDentalTechProps) => {
    const { userId } = useParams();
    const editableMode = !!!userId;

    const { updateTechAPI, techAPI2UserINfo } = useUserService();
    const { userInfo: currentUserInfo, updateUserInfoInStorage, updateUserInfoFromLocalStorage, setUserInfo: updateUserInfoInContext } = useAuthContext();
    const isOwnProfile = editableMode || Number(userId) === currentUserInfo?.id;
    const { getImagePath, makeDroppableCapable, deleteResumeApi, getResumeViewerUrl, state } = useFileManagement();
    const { getTech, incognitoModeApi } = useTechnicians();
    const { conditionFulfilled: isSmallDevice } = useWindowSizeMatch({
        condition: evaluators.lessThan(DEVICES_SIZES_IN_PX.medium),
    });
    const { onboardingStatus, nextOnboardingStep, remainingSteps, refreshOnboardingProgress } = useOnboardingProcess();
    const [regions, setRegions] = useState<TGenericGUIDName[]>([]);
    const [roles, setRoles] = useState<TEmployeeRole[]>([]);
    const isCandidate = currentUserInfo?.type === "tech";

    const [userInfo, setUserInfo] = useState<TApiTechnician>({
        id: -1,
        guid: "",
        name: "",
        inmycontactlist: false,
        enabled: true,
        incognito: false,
        images: {
            background: "",
            external: "",
            internal: "",
            logo: "",
            resume: "",
        },
        other: {
            desiredRegion: [],
            desiredSalaryPerYear: { amount: null, frequency: "yearly" },
            email: "",
            firstName: "",
            hobbies: [],
            imgUrl: "",
            interests: [],
            lastName: "",
            roles: [],
            yearsOfExperience: 0,
            linkedin: "",
            location: "",
            phone: "",
            secondaryEmail: "",
            zip: "",
            description: "",
            idealJob: "",
        },
        verifications: {
            phone: false,
            email: undefined,
        },
    });
    const [userExist, setUserExist] = useState<boolean>(true);
    const [backgroundImg, setBackgroundImg] = useState<string | undefined>(undefined);
    const [workingState, setWorkingState] = useState<TProcessStates>("done");
    const [successUpload, setSuccessUpload] = useState<boolean>(false);
    const [inappropriateContent, setInappropriateContent] = useState(false);
    const [unsupportedImageType, setUnsupportedImageType] = useState(false);
    const [uploadImageError, setUploadImageError] = useState("");
    const [isSomePartBeingEdited, setIsSomePartBeingEdited] = useState(false);
    const [resumeUploadError, setResumeUploadError] = useState("");
    const [isResumeViewerOpen, setIsResumeViewerOpen] = useState(false);
    const [resumeViewerUrl, setResumeViewerUrl] = useState("");
    const resumeViewerTitle = `${userInfo.name} Resume`;

    const refBackgroundPhoto = useRef<HTMLDivElement>(null);

    const updateTechAPIAndLocalStorage = async (data: TApiTechnician, name?: string) => {
        await updateTechAPI(data, name);
        const dataForStorage = techAPI2UserINfo(data, name);
        updateUserInfoInStorage(dataForStorage);
        updateUserInfoInContext(dataForStorage); //TODO: review this, it generates an unwanted refresh
        window.wwgTechInfo_dataWasModified = true;
        refreshOnboardingProgress();
    };

    const updateBackgroundImg = (newfile?: string, inappropriateContent?: boolean, unsupportedImageType?: boolean, errorMessage?: string) => {
        if (errorMessage) {
            window.scrollTo({ top: 0 });
            setUploadImageError(errorMessage);
            return;
        }
        if (inappropriateContent) {
            window.scrollTo({ top: 0 });
            setUploadImageError("");
            setInappropriateContent(true);
            setBackgroundImg(undefined);
        } else if (unsupportedImageType) {
            window.scrollTo({ top: 0 });
            setUploadImageError("");
            setUnsupportedImageType(true);
            setBackgroundImg(undefined);
        } else {
            if (!newfile) {
                setBackgroundImg(undefined);
            } else {
                const cacheKill = editable ? `?${getCacheKillQueryString()}` : "";
                setUploadImageError("");
                setInappropriateContent(false);
                setUnsupportedImageType(false);
                setBackgroundImg(getImagePath(newfile, "fixed", !editable ? userId : undefined) + cacheKill);
            }
        }
    };

    const updateResume = (newfile?: string, errorMessage?: string) => {
        if (errorMessage) {
            setResumeUploadError(errorMessage);
            setIsResumeViewerOpen(false);
            setResumeViewerUrl("");
            setSuccessUpload(false);
            return;
        }
        if (!newfile) {
            setResumeUploadError("");
            setIsResumeViewerOpen(false);
            setResumeViewerUrl("");
            setSuccessUpload(false);
        } else {
            setResumeUploadError("");
            setSuccessUpload(true);
        }
    };

    const closeResumeViewer = () => {
        setIsResumeViewerOpen(false);
        setResumeViewerUrl("");
    };

    const openResumeViewer = async () => {
        setResumeViewerUrl("");
        const secureUrl = await getResumeViewerUrl(userInfo.id);
        const viewerUrl = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(secureUrl)}`;
        setResumeViewerUrl(viewerUrl);
        setIsResumeViewerOpen(true);
    };

    const bringUserInfo = async () => {
        if (!paramUserExist) {
            setUserExist(false);
            return;
        }

        const info = techInfo !== undefined ? [techInfo] : await getTech(Number(userId) || (currentUserInfo?.id as number));
        if (info.length === 0) {
            setUserExist(false);
            return;
        }
        setUserInfo(info[0]);
        setUserExist(true);

        const extraValues = await Promise.all([getRegions(), getIndustryRoles()]);
        setRegions(extraValues[0]);
        setRoles(extraValues[1]);

        //ANGEL updateLogo(info[0]?.images.logo);
        //setLogo(info[0]?.images.logo);
        //ANGEL updateLogo(info[0]?.images.logo);
        updateBackgroundImg(info[0]?.images?.background);
        updateResume(info[0]?.images?.resume);

        // if (info[0].images.logo) {
        //     setLogo(getImagePath("logo", "fixed", info[0].guid) + "?" + getCacheKillQueryString());
        // }
        if (info[0]?.images?.background) {
            setBackgroundImg(getImagePath("background", "fixed", info[0].guid) + "?" + getCacheKillQueryString());
        }
        refreshOnboardingProgress();
        setWorkingState("done");
    };

    useEffect(() => {
        bringUserInfo();
        if (editable) {
            makeDroppableCapable(refBackgroundPhoto, "background", "image", "fixed", (newfile, inappropriateContent, unsupportedImageType, errorMessage) => {
                updateBackgroundImg(newfile, inappropriateContent, unsupportedImageType, errorMessage);
                refreshOnboardingProgress();
            });
        }

        return () => {
            if (window.wwgTechInfo_dataWasModified) {
                window.wwgTechInfo_dataWasModified = false;
                updateUserInfoFromLocalStorage();
            }
        };
    }, [userId]);

    const rolesList = (): TEmployeeRole[] => {
        if (!userInfo || !userInfo?.other?.roles) return [];

        return roles.filter((_role) => {
            return userInfo.other?.roles.findIndex((r) => r.id === _role.id) !== -1;
        });
    };

    const [isEditingName, setIsEditingName] = useState(false);

    const NameSection = () => {
        const nameRef = useRef<any>();
        const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
        const editableOnClickHandler = (e: any) => {
            if (!editable) return;
            if (isSomePartBeingEdited) return;
            e.preventDefault();
            e.stopPropagation();
            setIsEditingName(true);
            setIsSomePartBeingEdited(true);
        };

        const saveName = async () => {
            const newName = nameRef.current.value;
            setCurrWorkingState("working");
            setIsEditingName(false);
            setIsSomePartBeingEdited(false);
            await updateTechAPIAndLocalStorage(
                {
                    ...userInfo,
                },
                newName,
            );
            setUserInfo({
                ...userInfo,
                name: newName,
            });
            setCurrWorkingState("done");
        };

        const cancelHandler = async () => {
            nameRef.current.value = userInfo?.name;
            setIsEditingName(false);
            setIsSomePartBeingEdited(false);
        };

        return (
            <>
                {!isEditingName && (
                    <>
                        {userId && (
                            <div className={"text-break my-lg-4 mb-4 mt-2 " + (workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : "")}>
                                <h3 className={"fw-bold" + (!!userInfo.incognito ? " lab-can-not-see" : "")}>
                                    {workingState === "done" ? nameRef.current?.value || userInfo?.name : <>&nbsp;</>}
                                </h3>
                            </div>
                        )}
                        {editable && (
                            <div
                                className={"title-section-alignment text-break " + (workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : "")}
                            >
                                <div role="button" onClick={editableOnClickHandler} className="my-3 fs-4 fw-bold">
                                    {workingState === "done" ? nameRef.current?.value || userInfo?.name : <>&nbsp;</>}
                                </div>
                                {!isSomePartBeingEdited && (
                                    <div className="fileTooltip rightEdit " role="button" data-tooltip=" Edit " onClick={editableOnClickHandler}>
                                        <i className="fa-solid fa-pen-to-square fa-xs p-2 shadow editIcon" data-testid="edit-icon"></i>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
                {isEditingName && (
                    <div className="rounded p-3 shadow w-auto h-auto mb-3">
                        <input
                            className="text-center mb-2 form-control form-control-md"
                            ref={nameRef}
                            type="text"
                            defaultValue={userInfo?.name}
                            maxLength={50}
                        />
                        <div className="d-grid gap-2 mt-2 w-100 d-md-block">
                            <button className="btn btn-primary px-4" onClick={saveName}>
                                Save
                            </button>
                            <button className="btn btn-danger ms-md-2 px-3 " onClick={cancelHandler}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    };

    const [isEditingTechInfo, setIsEditingTechInfo] = useState(false);
    const refRegions = useRef<any>({});

    const TechInfoSection = () => {
        const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
        const editableOnClickHandler = (e: any) => {
            if (!editable) return;
            if (isSomePartBeingEdited) return;
            e.preventDefault();
            e.stopPropagation();
            setIsEditingTechInfo(true);
            setIsSomePartBeingEdited(true);
        };

        const [errorYears, setErrorYears] = useState("");
        const [errorSalary, setErrorSalary] = useState("");
        const [errorZip, setErrorZip] = useState("");
        const salaryFrequencyList: TSalaryFrequency[] = ["hourly", "yearly"];

        const saveRegions = async () => {
            const yearsValid = refYearsOfExperience.current.value;
            const salaryValid = refDesiredSalaryAmount.current.value;
            const zipValid = refZip.current.value;
            const newRegions = Object.keys(refRegions.current).filter((val) => refRegions.current[val]);
            const yearsAreInvalid = !isNumberFromString(yearsValid) && yearsValid.length > 0;
            const salaryIsInvalid = !isNumberFromString(salaryValid) && salaryValid.length > 0;
            const zipIsInvalid = !isNumberFromString(zipValid);

            let hasError = false;

            if (yearsAreInvalid) {
                setErrorYears("Please use numbers only for Years of Experience.");
                hasError = true;
            } else if (parseInt(yearsValid, 10) < 0 || parseInt(yearsValid, 10) > 100) {
                setErrorYears("Must be a number between 0 - 100");
                hasError = true;
            } else {
                setErrorYears("");
            }
            if (salaryIsInvalid) {
                setErrorSalary("Please use numbers only for Desired Pay.");
                hasError = true;
            } else {
                setErrorSalary("");
            }
            if (zipIsInvalid) {
                setErrorZip("Please use numbers only for your ZIP.");
                hasError = true;
            } else {
                setErrorZip("");
            }

            if (hasError) {
                return;
            }

            setCurrWorkingState("working");
            await updateTechAPIAndLocalStorage(
                {
                    ...userInfo,
                    other: {
                        ...userInfo.other,
                        desiredRegion: newRegions,
                        yearsOfExperience: refYearsOfExperience.current.value,
                        /*interests: [...interestPills.values],*/
                        hobbies: [...hobbiesPills.values],
                        desiredSalaryPerYear: { amount: refDesiredSalaryAmount.current.value, frequency: refDesiredSalaryFrequency.current.value },
                        location: refLocation.current.value,
                        zip: refZip.current.value,
                    },
                },
                undefined,
            );

            setUserInfo({
                ...userInfo,
                other: {
                    ...userInfo.other,
                    desiredRegion: newRegions,
                    yearsOfExperience: refYearsOfExperience.current.value,
                    /*interests: [...interestPills.values],*/
                    hobbies: [...hobbiesPills.values],
                    desiredSalaryPerYear: { amount: refDesiredSalaryAmount.current.value, frequency: refDesiredSalaryFrequency.current.value },
                    location: refLocation.current.value,
                    zip: refZip.current.value,
                },
            });
            setIsSomePartBeingEdited(false);
            setIsEditingTechInfo(false);
            setCurrWorkingState("done");
        };

        const cancelHandler = async () => {
            refRegions.current.value = [...(userInfo.other.desiredRegion || [])];
            refYearsOfExperience.current.value = userInfo?.other?.yearsOfExperience;
            refDesiredSalaryAmount.current.value = userInfo?.other?.desiredSalaryPerYear?.amount;
            refDesiredSalaryFrequency.current.value = userInfo?.other?.desiredSalaryPerYear?.frequency;
            //interestPills.setValues([...(userInfo?.other?.interests || [])]);
            hobbiesPills.setValues([...(userInfo?.other?.hobbies || [])]);
            setIsEditingTechInfo(false);
            setIsSomePartBeingEdited(false);
        };

        const refYearsOfExperience = useRef<any>({});
        const refDesiredSalaryAmount = useRef<any>({});
        const refDesiredSalaryFrequency = useRef<any>({});
        //const interestPills = usePillInput([]);
        const hobbiesPills = usePillInput([]);
        const refLocation = useRef<any>({});
        const refZip = useRef<any>({});
        let defaultValue: {
            [key: string]: boolean;
        } = {};

        useEffect(() => {
            const selectedRegions = userInfo.other?.desiredRegion.reduce((prev, curr) => {
                return {
                    ...prev,
                    [curr]: true,
                };
            }, defaultValue);

            refRegions.current = selectedRegions;
            refYearsOfExperience.current.value = userInfo?.other?.yearsOfExperience || "";
            refDesiredSalaryAmount.current.value = userInfo?.other?.desiredSalaryPerYear?.amount || "";
            refDesiredSalaryFrequency.current.value = userInfo?.other?.desiredSalaryPerYear?.frequency || "yearly";
            //interestPills.setValues([...(userInfo?.other?.interests || [])]);
            hobbiesPills.setValues([...(userInfo?.other?.hobbies || [])]);
            refLocation.current.value = userInfo?.other?.location || "";
            refZip.current.value = userInfo?.other?.zip || "";
        }, []);
        return (
            <>
                {!isEditingTechInfo && (
                    <>
                        <div className="title-section-alignment mb-3">
                            <h5 className="fw-bold m-0">
                                <i className="fa-solid fa-address-card me-3 text-info"></i>
                                PERSONAL PROFILE
                            </h5>
                            {editableMode && editable && !isSomePartBeingEdited && (
                                <div
                                    className="fileTooltip rightEdit ms-auto"
                                    role="button"
                                    data-tooltip=" Edit "
                                    onClick={editableOnClickHandler}
                                    data-testid="edit-icon"
                                >
                                    <i className="fa-solid fa-pen-to-square fa-xs p-2 shadow editIcon" data-testid="edit-icon"></i>
                                </div>
                            )}
                        </div>
                        <div className={"text-break " + (workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : "")}>
                            {!!userInfo?.other.yearsOfExperience && (
                                <div className="row mb-3">
                                    <div className="col-5">
                                        <strong>Experience {isCandidate && " *"}</strong>
                                    </div>
                                    <div className="col-7">{userInfo?.other?.yearsOfExperience} years</div>
                                </div>
                            )}
                            <div className="row mb-3">
                                <div className="col-5">
                                    <strong>Desired Region</strong>
                                </div>
                                <div className="col-7">
                                    {regions
                                        .filter(
                                            (region) =>
                                                refRegions?.current?.value?.includes(region.guid) || userInfo?.other?.desiredRegion?.includes(region.guid),
                                        )
                                        .map((region) => region.name)
                                        .join(", ")}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-5">
                                    <strong>Desired Pay</strong>
                                </div>
                                <div className="col-7">
                                    {!!userInfo?.other?.desiredSalaryPerYear ? (
                                        <small>
                                            ${" "}
                                            {currencyFormatter.format(
                                                Number(userInfo.other.desiredSalaryPerYear.amount ?? userInfo.other.desiredSalaryPerYear),
                                            )}{" "}
                                            {userInfo.other.desiredSalaryPerYear.frequency ?? "yearly"}
                                        </small>
                                    ) : (
                                        <small className="no-data">No pay to show</small>
                                    )}
                                </div>
                            </div>
                            {editableMode && (
                                <div className="row mb-3">
                                    <div className="col-5">
                                        <strong>Current Address</strong>
                                        <div className="d-inline-flex align-items-center ms-2">
                                            <div
                                                data-bs-toggle="collapse"
                                                data-bs-target="#currentAddressInfoReadonly"
                                                aria-expanded="false"
                                                aria-controls="currentAddressInfoReadonly"
                                                role="button"
                                            >
                                                <i className="fa-solid fa-circle-info text-info"></i>
                                            </div>
                                        </div>
                                        <div className="collapse p-0" id="currentAddressInfoReadonly">
                                            <small className="text-muted d-block helper-text">
                                                This address is for internal use only and is not shown publicly.
                                            </small>
                                        </div>
                                    </div>
                                    <div className="col-7">
                                        {!!userInfo?.other?.location ? (
                                            <span>{userInfo.other.location}</span>
                                        ) : (
                                            <small className="no-data">No address to show</small>
                                        )}
                                    </div>
                                </div>
                            )}
                            {!!userInfo?.other.zip && (
                                <div className="row mb-3">
                                    <div className="col-5">
                                        <strong>ZIP Code</strong>
                                    </div>
                                    <div className="col-7">{userInfo?.other?.zip}</div>
                                </div>
                            )}
                            {/* <div className="row mb-3">
                                <div className="col-5">
                                    <strong>Interests</strong>
                                </div>
                                <div className="col-7">
                                    {userInfo?.other?.interests.map(
                                        (interest, index) => (
                                            <span
                                                key={index}
                                                className="badge rounded-pill text-bg-info text-white me-2 text-wrap mt-1"
                                            >
                                                {interest}
                                            </span>
                                        )
                                    )}
                                </div>
                            </div> */}
                            <div className="row mb-3">
                                <span id="add_hobbies-progress"></span>
                                <div className="col-5">
                                    <strong>Interests & Hobbies {isCandidate && " *"}</strong>
                                </div>
                                <div className="col-7">
                                    {userInfo?.other?.hobbies.map((hobbies, index) => (
                                        <span key={index} className="badge rounded-pill text-bg-info text-white me-2 text-wrap mt-1">
                                            {hobbies}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {isEditingTechInfo && (
                    <div className="row align-items-end">
                        <div className="col-md-5">
                            <h6>Years of Experience: </h6>
                        </div>
                        <div className="col-md-6">
                            <input
                                className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading form-control" : "form-control"}
                                ref={refYearsOfExperience}
                                type="text"
                                defaultValue={userInfo?.other?.yearsOfExperience}
                                maxLength={3}
                            />
                        </div>
                        {errorYears && (
                            <div className="col-12">
                                <p className="text-danger m-0">
                                    <small>{errorYears}</small>
                                </p>
                            </div>
                        )}
                        <div className="col-md-5 align-self-start mt-4 my-md-5">
                            <h6>Interested in working in:</h6>
                        </div>
                        <div className="col-md-7 mb-4 my-md-5">
                            <RegionsCheckListNoFormik
                                regions={regions}
                                checkedList={refRegions.current}
                                refs={refRegions}
                                masterKey={"EditRegionsTechProfile"}
                            />
                        </div>
                        <div className="col-md-5">
                            <h6>Desired Pay:</h6>
                        </div>
                        <div className="col-md-6 d-flex align-items-start flex-column">
                            <div className="d-flex align-items-center gap-2 w-100">
                                <p className="mb-0">$</p>
                                <input
                                    className={"form-control rounded " + (workingState !== "done" || (currWorkingState !== "done" && " cssLoading "))}
                                    ref={refDesiredSalaryAmount}
                                    type="text"
                                    defaultValue={userInfo?.other?.desiredSalaryPerYear?.amount ?? ""}
                                    maxLength={25}
                                />
                                <select
                                    className={"form-select rounded " + (workingState !== "done" || (currWorkingState !== "done" && " cssLoading "))}
                                    ref={refDesiredSalaryFrequency}
                                    defaultValue={userInfo?.other?.desiredSalaryPerYear?.frequency}
                                >
                                    {salaryFrequencyList.map((freq) => (
                                        <option key={freq} value={freq}>
                                            {freq.charAt(0).toUpperCase() + freq.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {errorSalary && (
                            <div className="col-12">
                                <p className="text-danger m-0">
                                    <small>{errorSalary}</small>
                                </p>
                            </div>
                        )}
                        <hr className="mt-3" />
                        <div className="col-md-5 pt-md-2">
                            <h6 className="mb-1">Current Address:</h6>
                            <small className="text-muted d-block helper-text">This address is for internal use only and is not shown publicly.</small>
                        </div>
                        <div className="col-md-7 mt-2 mt-md-0">
                            <textarea
                                className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading form-control" : "form-control"}
                                placeholder="Street Address, City, State"
                                ref={refLocation}
                                defaultValue={userInfo?.other?.location}
                                maxLength={100}
                                rows={2}
                            />
                        </div>
                        <div className="col-md-5 mt-4 pt-md-2">
                            <h6 className="mb-1">
                                ZIP code: <small className="fst-italic fw-bold">(required)</small>{" "}
                            </h6>
                        </div>
                        <div className="col-md-7 position-relative mt-1 mt-md-4">
                            <input
                                className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading form-control " : "form-control"}
                                placeholder="ZIP code is required"
                                ref={refZip}
                                type="text"
                                defaultValue={userInfo?.other?.zip}
                                maxLength={20}
                            />
                        </div>
                        {errorZip && (
                            <div className="col-12">
                                <p className="text-danger m-0">
                                    <small>{errorZip}</small>
                                </p>
                            </div>
                        )}
                        <hr className="my-3" />
                        <div className="p-3 my-3 rounded ai-container">
                            <h6 className="mb-2">
                                <i className="fa-solid fa-wand-magic-sparkles me-3 fa-lg" style={{ color: "#6468fe" }}></i>
                                Add your interests and hobbies
                            </h6>
                            <p className="text-muted small mb-3">
                                Our AI uses this information to suggest dental employers that are a better fit for you. Choose from the list or add your own.
                            </p>
                            <PillInput hook={hobbiesPills} />
                        </div>
                        <div className="d-grid gap-3 mt-2 w-100 d-md-block">
                            <button className="btn btn-primary px-4" onClick={saveRegions}>
                                {workingState === "working" || currWorkingState === "working" ? <Spinner spinnerColor="text-primary" /> : "Save"}
                            </button>
                            <button className="btn btn-danger px-3 ms-md-2" onClick={cancelHandler}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    };

    const [isEditingRoles, setIsEditingRoles] = useState(false);
    const refPositions = useRef<any>({});

    const PositionsSection = () => {
        const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
        const editableOnClickHandler = (e: any) => {
            if (!editable) return;
            e.preventDefault();
            e.stopPropagation();
            setIsEditingRoles(true);
            setIsSomePartBeingEdited(true);
        };

        const saveRoles = async () => {
            const newRoles: TEmployeeRole[] = Object.keys(refPositions.current)
                .filter((val) => refPositions.current[val])
                .map((p) => ({
                    id: Number(p),
                    name: "",
                    skills: [],
                    skills_count: 0,
                }));
            setIsSomePartBeingEdited(false);

            setCurrWorkingState("working");
            await updateTechAPIAndLocalStorage(
                {
                    ...userInfo,
                    other: {
                        ...userInfo.other,
                        roles: newRoles,
                    },
                },
                undefined,
            );

            setUserInfo({
                ...userInfo,
                other: {
                    ...userInfo.other,
                    roles: newRoles,
                },
            });
            setIsEditingRoles(false);
            setCurrWorkingState("done");
        };

        const cancelHandler = async () => {
            setIsEditingRoles(false);
            setIsSomePartBeingEdited(false);
        };

        let defaultValue: {
            [key: string]: boolean;
        } = {};

        useEffect(() => {
            const selectedPositions = userInfo.other?.roles.reduce((prev, curr) => {
                return {
                    ...prev,
                    [curr.id + ""]: true,
                };
            }, defaultValue);

            refPositions.current = selectedPositions;
        }, []);

        return (
            <>
                {!isEditingRoles && (
                    <>
                        <div className="title-section-alignment mb-3">
                            <h5 className="fw-bold m-0">
                                <i className="fa-solid fa-award me-3 text-info"></i>
                                ROLES I'M LOOKING FOR
                            </h5>
                            {editableMode && !isSomePartBeingEdited && (
                                <div
                                    className="fileTooltip rightEdit ms-auto"
                                    role="button"
                                    data-tooltip=" Edit "
                                    onClick={editableOnClickHandler}
                                    data-testid="edit-icon"
                                >
                                    <i className="fa-solid fa-pen-to-square fa-xs p-2 shadow editIcon" data-testid="edit-icon"></i>
                                </div>
                            )}
                        </div>
                        <div className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : "text-start"}>
                            {rolesList().map((pos) => (
                                <span key={pos.id + pos.name} className="badge rounded-pill bg-white border-2 border-info border text-dark me-2 mb-2 fs-6">
                                    {" "}
                                    {pos.name}
                                </span>
                            ))}
                        </div>
                    </>
                )}
                {isEditingRoles && (
                    <div className="p-3 mb-3">
                        <h6 className="fw-bold"> ROLES I'M LOOKING FOR: </h6>
                        <div className={"" + (workingState !== "done" || currWorkingState !== "done" ? " cssLoading" : "")}>
                            <div className="row">
                                <PositionsCheckListNoFormik roles={roles} checkedList={refPositions.current} refs={refPositions} wideColumns={true} />
                            </div>

                            <div className="d-grid gap-3 mt-3 w-100 d-md-block">
                                <button className="btn btn-primary px-4" onClick={saveRoles}>
                                    Save
                                </button>
                                <button className="btn btn-danger ms-md-2 px-4" onClick={cancelHandler}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    };

    const InviteButtons = () => {
        const { invitationsList } = useNotificationContext();
        const userInvitation = invitationsList.find((p) => p.from === userInfo.id);
        return (
            <div className="mb-2">
                {!!userInfo?.invitationsenttome && userInvitation && (
                    <ConnectionRequestInfoAndButtons
                        userId={userInfo.id}
                        wideStyle={false}
                        userName={userInfo.name}
                        invitationMessage={userInvitation.message}
                    />
                )}
                {!userInfo?.invitationsenttome && userInfo.invitationsentstate === "accepted" && (
                    <p className="invitation-accepted text-center">
                        <small>
                            <img
                                src="/images/global/connected.svg"
                                alt="connected"
                                style={{
                                    height: "1rem",
                                    marginBottom: "0.3rem",
                                    marginRight: ".3rem",
                                }}
                            />{" "}
                            Invitation accepted
                        </small>
                    </p>
                )}
                {!userInfo?.invitationsenttome && userInfo.invitationsentstate !== "accepted" && (
                    <p>
                        <InviteButton userToInviteId={Number(userId)} userToInviteInvitationState={userInfo?.invitationsentstate} userType="tech" />
                    </p>
                )}
            </div>
        );
    };

    const [isEditingExperience, setIsEditingExperience] = useState(false);
    const [newExperience, setNewExperience] = useState<TExperience[]>([]);
    const [currentExperience, setCurrentExperience] = useState<number | null>(null);
    const [copiedExperience, setCopiedExperience] = useState<TExperience | null>(null);
    const [spinnerExperienceState, setSpinnerExperienceState] = useState<TProcessStates>("done");

    const ExperienceSection = () => {
        const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
        const [dateExperienceValidationError, setDateExperienceValidationError] = useState<boolean>(false);
        const [experienceValidationError, setExperienceValidationError] = useState<string>("");

        const isExperienceRowComplete = (experienceRow: TExperience) => {
            return experienceRow.Employer.trim() !== "" && experienceRow.position.trim() !== "";
        };

        const hasAllRequiredExperienceFields = (experienceRows: TExperience[]): boolean => {
            const hasIncompleteExperience = experienceRows.some((exp) => !isExperienceRowComplete(exp));

            if (hasIncompleteExperience) {
                setExperienceValidationError("Please complete all experience fields before saving.");
                return false;
            }

            return true;
        };

        const saveExperience = async () => {
            userInfo.experience = {
                experience: [...(userInfo?.experience?.experience || [])],
            };
            setCurrWorkingState("working");
            setSpinnerExperienceState("working");
            await updateTechAPIAndLocalStorage(
                {
                    ...userInfo,
                },
                undefined,
            );

            setUserInfo({
                ...userInfo,
            });
            setCurrWorkingState("done");
            setSpinnerExperienceState("done");
            setIsEditingExperience(false);
            setIsSomePartBeingEdited(false);
            setExperienceValidationError("");
        };
        const ExperienceRow = ({ experienceRow, rowIsEditing, idx }: { experienceRow: TExperience; rowIsEditing: boolean; idx: number }) => {
            const validateDates = (yearStart: number, yearEnd: number | null): boolean => {
                const currentYear = new Date().getFullYear();
                if (yearStart < 0 || yearStart > currentYear || (yearEnd && (yearEnd < yearStart || yearEnd > currentYear))) {
                    setDateExperienceValidationError(true);
                    return false;
                }
                setDateExperienceValidationError(false);
                return true;
            };

            const debouncedValidateDates = debounce({
                func: (start: number, end: number) => {
                    validateDates(start, end);
                },
                delay: 1500,
            });

            let yearLine = undefined;

            if (experienceRow.yearStart) {
                if (experienceRow.yearEnd && experienceRow.yearEnd !== experienceRow.yearStart) {
                    yearLine = `${experienceRow.yearStart} - ${experienceRow.yearEnd}`;
                } else {
                    yearLine = experienceRow.yearStart + "";
                }
            }
            return (
                <>
                    {!rowIsEditing && (
                        <div className="text-break">
                            <div>
                                <p className="title-section-alignment mb-2">
                                    <strong>{experienceRow.Employer}</strong>
                                    {editable && !isEditingExperience && !isSomePartBeingEdited && (
                                        <div className="d-none d-md-flex">
                                            <div
                                                className=""
                                                style={{
                                                    height: "25px",
                                                    width: "25px",
                                                }}
                                                role="button"
                                                onClick={() => editExperience(idx)}
                                            >
                                                <i className="fa-solid fa-pen-to-square fa-xs shadow editIcon" data-testid="edit-icon"></i>
                                            </div>
                                            <div
                                                className="ms-2 d-flex justify-content-center align-items-center bg-danger rounded-circle"
                                                style={{
                                                    height: "25px",
                                                    width: "25px",
                                                }}
                                                onClick={() => removeExperience(idx)}
                                                role="button"
                                            >
                                                <i className="fa-regular fa-trash-can fa-xs text-white" data-testid="delete-icon"></i>
                                            </div>
                                        </div>
                                    )}
                                </p>
                            </div>
                            {!!experienceRow.position && (
                                <p>
                                    {experienceRow.position}
                                    {!!yearLine && (
                                        <>
                                            <br />
                                            {yearLine}
                                        </>
                                    )}
                                </p>
                            )}
                        </div>
                    )}
                    {rowIsEditing && (
                        <div>
                            <div className="row g-2 align-items-md-center mb-2">
                                <div className="col-12 col-md-4">
                                    <label className="form-label mb-0 small fw-semibold">Employer name</label>
                                </div>
                                <div className="col-12 col-md-8">
                                    <input
                                        className="form-control"
                                        type="text"
                                        onChange={(e) => (experienceRow.Employer = e.target.value)}
                                        defaultValue={experienceRow.Employer}
                                        maxLength={50}
                                    />
                                </div>
                            </div>
                            <div className="row g-2 align-items-md-center mb-2">
                                <div className="col-12 col-md-4">
                                    <label className="form-label mb-0 small fw-semibold">Position</label>
                                </div>
                                <div className="col-12 col-md-8">
                                    <input
                                        className="form-control"
                                        type="text"
                                        onChange={(e) => (experienceRow.position = e.target.value)}
                                        defaultValue={experienceRow.position}
                                        maxLength={100}
                                    />
                                </div>
                            </div>
                            <div className="row g-2 mb-2">
                                <div className="col-12 col-md-4">
                                    <label className="form-label mb-0 small fw-semibold">Dates</label>
                                </div>
                                <div className="col-12 col-md-8">
                                    <div className="row g-2 mb-2">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label mb-1 small text-muted fw-bold">Start year</label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                min={0}
                                                max={new Date().getFullYear()}
                                                placeholder="YYYY"
                                                defaultValue={experienceRow.yearStart || undefined}
                                                onChange={(e) => {
                                                    const yearStart = Number(e.target.value) || 0;
                                                    experienceRow.yearStart = yearStart;
                                                    debouncedValidateDates(yearStart, experienceRow.yearEnd);
                                                }}
                                            />
                                            <small className="text-muted d-block mt-1 helper-text">Year you started this role.</small>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label mb-1 small text-muted fw-bold">End year</label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                min={0}
                                                max={new Date().getFullYear()}
                                                placeholder="YYYY"
                                                defaultValue={experienceRow.yearEnd || undefined}
                                                onChange={(e) => {
                                                    const yearEnd = Number(e.target.value) || 0;
                                                    experienceRow.yearEnd = yearEnd;
                                                    debouncedValidateDates(experienceRow.yearStart, yearEnd);
                                                }}
                                            />
                                            <small className="text-muted d-block mt-1 helper-text">
                                                Year you ended this role. Use the current year if you still work here.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                {dateExperienceValidationError && (
                                    <div className="badge rounded-pill text-bg-warning mt-2 w-100 text-wrap">
                                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                        Invalid date range. Please check your inputs.
                                    </div>
                                )}
                                {!!experienceValidationError && (
                                    <div className="badge rounded-pill text-bg-warning my-2 w-100 text-wrap">
                                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                        {experienceValidationError}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            );
        };

        const addExperience = () => {
            setExperienceValidationError("");
            setIsEditingExperience(true);
            setIsSomePartBeingEdited(true);
            setNewExperience([
                ...newExperience,
                {
                    Employer: "",
                    position: "",
                    yearEnd: 0,
                    yearStart: 0,
                },
            ]);
        };

        const saveNewExperience = async () => {
            const oldExperience = userInfo?.experience?.experience || [];
            if (!hasAllRequiredExperienceFields(newExperience)) return;
            userInfo.experience = {
                experience: [...oldExperience, ...newExperience],
            };
            setNewExperience([]);
            await saveExperience();
        };

        const removeExperience = async (idx: number) => {
            setExperienceValidationError("");
            const oldExperience = userInfo?.experience?.experience || [];
            oldExperience.splice(idx, 1);

            userInfo.experience = { experience: [...oldExperience] };
            setIsEditingExperience(true);
            setIsSomePartBeingEdited(true);
            await saveExperience();
        };

        const cancelNewExperience = () => {
            setIsEditingExperience(false);
            setNewExperience([]);
            setIsSomePartBeingEdited(false);
            setExperienceValidationError("");
        };

        const editExperience = (idx: number) => {
            setExperienceValidationError("");
            setIsEditingExperience(true);
            setIsSomePartBeingEdited(true);
            setCurrentExperience(idx);

            const originalExperience = userInfo.experience?.experience?.[idx];
            if (originalExperience) {
                const copyExperience: TExperience = { ...originalExperience };
                setCopiedExperience(copyExperience);
            }
        };

        const saveEditedExperience = async (_idx: number) => {
            const updatedExperience = [...(userInfo?.experience?.experience || [])];
            if (!hasAllRequiredExperienceFields(updatedExperience)) return;
            setUserInfo({
                ...userInfo,
                experience: {
                    ...userInfo.experience,
                    experience: updatedExperience,
                },
            });
            await saveExperience();
            setCurrentExperience(null);
        };

        const cancelEditExperience = () => {
            const oldExperience = [...(userInfo.experience?.experience || [])];
            if (copiedExperience !== null && currentExperience !== null) {
                oldExperience[currentExperience] = copiedExperience;
                setUserInfo({
                    ...userInfo,
                    experience: {
                        ...userInfo.experience,
                        experience: oldExperience,
                    },
                });
                setCopiedExperience(null);
            }
            setCurrentExperience(null);
            setIsEditingExperience(false);
            setIsSomePartBeingEdited(false);
            setExperienceValidationError("");
        };

        return (
            <div>
                <h5 className="fw-bold mb-3">
                    <i className="fa-solid fa-briefcase me-3 text-info"></i>
                    EXPERIENCE {isCandidate && " *"}
                </h5>
                {
                    <ul className="list-group list-group-flush rounded">
                        {!!!userInfo.experience?.experience && (
                            <p style={{ color: "#a5a5a5" }} className="m-0">
                                <small> No experience provided yet.</small>
                            </p>
                        )}
                        {!!userInfo.experience?.experience &&
                            userInfo.experience.experience.map((e, idx) => (
                                <li
                                    data-testid="experience_item"
                                    key={"li_experience_current_" + idx}
                                    className={
                                        "list-group-item py-3 " +
                                        (workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : "") +
                                        (!!userInfo.incognito && !editable ? " lab-can-not-see" : "")
                                    }
                                >
                                    {currentExperience === idx ? (
                                        <>
                                            <ExperienceRow key={"li_experience_current_row" + idx} rowIsEditing={true} experienceRow={e} idx={idx} />
                                            <div className="d-grid gap-2 mt-2 w-100 d-md-block">
                                                <button
                                                    className="btn btn-primary px-4"
                                                    disabled={dateExperienceValidationError}
                                                    onClick={() => saveEditedExperience(idx)}
                                                >
                                                    Save
                                                </button>
                                                <button className="btn btn-danger ms-md-2" onClick={() => cancelEditExperience()}>
                                                    {" "}
                                                    Cancel
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <ExperienceRow key={"li_experience_current_row" + idx} rowIsEditing={false} experienceRow={e} idx={idx} />
                                            {editable && !isEditingExperience && !isSomePartBeingEdited && (
                                                <div className="d-flex d-md-none justify-content-end align-items-center gap-2 mt-3 pt-2">
                                                    <button className="btn btn-light btn-sm px-3 shadow-sm" onClick={() => editExperience(idx)}>
                                                        <i className="fa-solid fa-pen-to-square me-2"></i>
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-link btn-sm text-danger text-decoration-none px-2"
                                                        onClick={() => removeExperience(idx)}
                                                    >
                                                        <i className="fa-regular fa-trash-can me-2"></i>
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </li>
                            ))}
                        {newExperience.map((e, idx) => (
                            <li key={"li_newExperience_" + idx} className="list-group-item py-3">
                                <ExperienceRow rowIsEditing={true} key={"li_newExperience_row" + idx} experienceRow={e} idx={idx} />
                                {editable && isEditingExperience && (
                                    <>
                                        <div className="col text-end">
                                            <button className="btn btn-primary px-4" disabled={dateExperienceValidationError} onClick={saveNewExperience}>
                                                Save
                                            </button>
                                            <button className="btn btn-danger ms-2" onClick={cancelNewExperience}>
                                                Cancel
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                }

                {editable && !isEditingExperience && !isSomePartBeingEdited && (
                    <div className="row">
                        <div className="col mt-3">
                            <button data-testid="experience_add" className="w-100 btn btn-outline-info" onClick={addExperience}>
                                Add experience
                            </button>
                        </div>
                    </div>
                )}
                {editable && (
                    <div className="row">
                        <div className="col mt-3">
                            {spinnerExperienceState === "working" ? (
                                <div className="text-center fs-2 text-primary">
                                    <Spinner spinnerColor="text-primary" />
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const [isEditingEducation, setIsEditingEducation] = useState(false);
    const [newEducation, setNewEducation] = useState<TEducation[]>([]);
    const [currentEducation, setCurrentEducation] = useState<number | null>(null);
    const [copiedEducation, setCopiedEducation] = useState<TEducation | null>(null);
    const [spinnerEducationState, setSpinnerEducationState] = useState<TProcessStates>("done");

    const EducationSection = () => {
        const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
        const [dateEducationValidationError, setDateEducationValidationError] = useState<boolean>(false);
        const [educationValidationError, setEducationValidationError] = useState<string>("");

        const isEducationRowComplete = (educationRow: TEducation) => {
            return educationRow.schoolName.trim() !== "" && educationRow.degree_or_certificate.trim() !== "";
        };

        const hasAllRequiredEducationFields = (educationRows: TEducation[]): boolean => {
            const hasIncompleteEducation = educationRows.some((edu) => !isEducationRowComplete(edu));

            if (hasIncompleteEducation) {
                setEducationValidationError("Please complete all education fields before saving.");
                return false;
            }

            return true;
        };

        const saveEducation = async () => {
            userInfo.education = {
                education: [...(userInfo?.education?.education || [])],
            };
            setCurrWorkingState("working");
            setSpinnerEducationState("working");
            await updateTechAPIAndLocalStorage(
                {
                    ...userInfo,
                },
                undefined,
            );

            setUserInfo({
                ...userInfo,
            });
            setCurrWorkingState("done");
            setSpinnerEducationState("done");
            setIsEditingEducation(false);
            setIsSomePartBeingEdited(false);
            setEducationValidationError("");
        };
        const EducationRow = ({ educationRow, rowIsEditing, idx }: { educationRow: TEducation; rowIsEditing: boolean; idx: number }) => {
            const validateDates = (yearStart: number, yearEnd: number | null): boolean => {
                const currentYear = new Date().getFullYear();
                if (yearStart < 0 || yearStart > currentYear || (yearEnd && (yearEnd < yearStart || yearEnd > currentYear))) {
                    setDateEducationValidationError(true);
                    return false;
                }
                setDateEducationValidationError(false);
                return true;
            };

            const debouncedValidateDates = debounce({
                func: (start: number, end: number) => {
                    validateDates(start, end);
                },
                delay: 1500,
            });

            let yearLine = undefined;

            if (educationRow.yearStart) {
                if (educationRow.yearEnd && educationRow.yearEnd !== educationRow.yearStart) {
                    yearLine = `${educationRow.yearStart} - ${educationRow.yearEnd}`;
                } else {
                    yearLine = educationRow.yearStart + "";
                }
            }
            return (
                <>
                    {!rowIsEditing && (
                        <div className="text-break">
                            <div>
                                <p className="d-flex justify-content-between mb-2">
                                    <strong>{educationRow.schoolName}</strong>
                                    {editable && !isEditingEducation && !isSomePartBeingEdited && (
                                        <div className="d-none d-md-flex">
                                            <div
                                                className=""
                                                style={{
                                                    height: "25px",
                                                    width: "25px",
                                                }}
                                                onClick={() => editEducation(idx)}
                                                role="button"
                                            >
                                                <i className="fa-solid fa-pen-to-square fa-xs shadow editIcon" data-testid="edit-icon"></i>
                                            </div>
                                            <div
                                                className="ms-2 d-flex justify-content-center align-items-center bg-danger rounded-circle"
                                                style={{
                                                    height: "25px",
                                                    width: "25px",
                                                }}
                                                onClick={() => removeEducation(idx)}
                                                role="button"
                                            >
                                                <i className="fa-regular fa-trash-can fa-xs text-white" data-testid="delete-icon"></i>
                                            </div>
                                        </div>
                                    )}
                                </p>
                            </div>
                            {!!educationRow.degree_or_certificate && (
                                <p>
                                    {educationRow.degree_or_certificate}
                                    {!!yearLine && (
                                        <>
                                            <br />
                                            {yearLine}
                                        </>
                                    )}
                                </p>
                            )}
                        </div>
                    )}
                    {rowIsEditing && (
                        <div>
                            <div className="row g-2 align-items-md-center mb-2">
                                <div className="col-12 col-md-4">
                                    <label className="form-label mb-0 small fw-semibold">School name</label>
                                </div>
                                <div className="col-12 col-md-8">
                                    <input
                                        className="form-control"
                                        type="text"
                                        onChange={(e) => (educationRow.schoolName = e.target.value)}
                                        defaultValue={educationRow.schoolName}
                                        maxLength={50}
                                    />
                                </div>
                            </div>
                            <div className="row g-2 align-items-md-center mb-2">
                                <div className="col-12 col-md-4">
                                    <label className="form-label mb-0 small fw-semibold">Degree, title, or certificate</label>
                                </div>
                                <div className="col-12 col-md-8">
                                    <input
                                        className="form-control"
                                        type="text"
                                        onChange={(e) => (educationRow.degree_or_certificate = e.target.value)}
                                        defaultValue={educationRow.degree_or_certificate}
                                        maxLength={100}
                                    />
                                </div>
                            </div>
                            <div className="row g-2 mb-2">
                                <div className="col-12 col-md-4">
                                    <label className="form-label mb-0 small fw-semibold">Dates</label>
                                </div>
                                <div className="col-12 col-md-8">
                                    <div className="row g-2 mb-2">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label mb-1 small text-muted fw-bold">Start year</label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                min={0}
                                                max={new Date().getFullYear()}
                                                placeholder="YYYY"
                                                defaultValue={educationRow.yearStart || undefined}
                                                onChange={(e) => {
                                                    const yearStart = Number(e.target.value) || 0;
                                                    educationRow.yearStart = yearStart;
                                                    debouncedValidateDates(yearStart, educationRow.yearEnd);
                                                }}
                                            />
                                            <small className="text-muted d-block mt-1 helper-text">Year you started this program.</small>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label mb-1 small text-muted fw-bold">End year</label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                defaultValue={educationRow.yearEnd || undefined}
                                                min={0}
                                                max={new Date().getFullYear()}
                                                placeholder="YYYY"
                                                onChange={(e) => {
                                                    const yearEnd = Number(e.target.value) || 0;
                                                    educationRow.yearEnd = yearEnd;
                                                    debouncedValidateDates(educationRow.yearStart, yearEnd);
                                                }}
                                            />
                                            <small className="text-muted d-block mt-1 helper-text">
                                                Year you ended this program. Use the current year if you are still enrolled.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                {dateEducationValidationError && (
                                    <div className="badge rounded-pill text-bg-warning mt-2 w-100 text-wrap">
                                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                        Invalid date range. Please check your inputs.
                                    </div>
                                )}
                                {!!educationValidationError && (
                                    <div className="badge rounded-pill text-bg-warning my-2 w-100 text-wrap">
                                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                        {educationValidationError}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            );
        };

        const addEducation = () => {
            setEducationValidationError("");
            setIsEditingEducation(true);
            setIsSomePartBeingEdited(true);
            setNewEducation([
                ...newEducation,
                {
                    degree_or_certificate: "",
                    schoolName: "",
                    yearEnd: 0,
                    yearStart: 0,
                },
            ]);
        };

        const saveNewEducation = async () => {
            const oldEducation = userInfo?.education?.education || [];
            if (!hasAllRequiredEducationFields(newEducation)) return;

            userInfo.education = {
                education: [...oldEducation, ...newEducation],
            };
            setNewEducation([]);
            await saveEducation();
        };

        const removeEducation = async (idx: number) => {
            setEducationValidationError("");
            const oldEducation = userInfo?.education?.education || [];
            oldEducation.splice(idx, 1);
            userInfo.education = { education: [...oldEducation] };
            setIsEditingEducation(true);
            setIsSomePartBeingEdited(true);
            await saveEducation();
        };

        const cancelNewEducation = () => {
            setIsEditingEducation(false);
            setNewEducation([]);
            setIsSomePartBeingEdited(false);
            setEducationValidationError("");
        };

        const editEducation = (idx: number) => {
            setEducationValidationError("");
            setIsEditingEducation(true);
            setIsSomePartBeingEdited(true);
            setCurrentEducation(idx);
            const originalEducation = userInfo.education?.education[idx];
            if (originalEducation) {
                const copyEducation: TEducation = { ...originalEducation };
                setCopiedEducation(copyEducation);
            }
        };

        const saveEditedEducation = async (_idx: number) => {
            const newEducation = [...(userInfo?.education?.education || [])];
            if (!hasAllRequiredEducationFields(newEducation)) return;
            setUserInfo({
                ...userInfo,
                education: {
                    ...userInfo.education,
                    education: newEducation,
                },
            });
            await saveEducation();
            setCurrentEducation(null);
        };

        const cancelEditEducation = () => {
            const oldEducation = [...(userInfo.education?.education || [])];
            if (copiedEducation !== null && currentEducation !== null) {
                oldEducation[currentEducation] = copiedEducation;
                setUserInfo({
                    ...userInfo,
                    education: {
                        ...userInfo.education,
                        education: oldEducation,
                    },
                });
                setCurrentEducation(null);
                setCopiedEducation(null);
            }
            setIsEditingEducation(false);
            setIsSomePartBeingEdited(false);
            setEducationValidationError("");
        };

        return (
            <>
                <div>
                    <h5 className="fw-bold mb-3">
                        <i className="fa-solid fa-book me-3 text-info"></i>
                        EDUCATION {isCandidate && " *"}
                    </h5>
                    {
                        <ul className="list-group list-group-flush rounded">
                            {!!!userInfo.education?.education && (
                                <p style={{ color: "#a5a5a5" }} className="m-0">
                                    <small> No education provided yet.</small>
                                </p>
                            )}
                            {!!userInfo.education?.education &&
                                userInfo.education.education.map((e, idx) => (
                                    <li
                                        data-testid="education_item"
                                        key={"li_education_current_" + idx}
                                        className={
                                            "list-group-item py-3 " +
                                            (workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : "") +
                                            (!!userInfo.incognito && !editable ? " lab-can-not-see" : "")
                                        }
                                    >
                                        {currentEducation === idx ? (
                                            <>
                                                <EducationRow key={"li_education_current_row" + idx} rowIsEditing={true} educationRow={e} idx={idx} />
                                                <div className="d-grid gap-2 mt-2 w-100 d-md-block">
                                                    <button
                                                        className="btn btn-primary px-4"
                                                        onClick={() => saveEditedEducation(idx)}
                                                        disabled={dateEducationValidationError}
                                                    >
                                                        Save
                                                    </button>
                                                    <button className="btn btn-danger ms-md-2" onClick={() => cancelEditEducation()}>
                                                        {" "}
                                                        Cancel
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <EducationRow key={"li_education_current_row" + idx} rowIsEditing={false} educationRow={e} idx={idx} />
                                                {editable && !isEditingEducation && !isSomePartBeingEdited && (
                                                    <div className="d-flex d-md-none justify-content-end align-items-center gap-2 mt-3 pt-2">
                                                        <button className="btn btn-light btn-sm px-3 shadow-sm" onClick={() => editEducation(idx)}>
                                                            <i className="fa-solid fa-pen-to-square me-2"></i>
                                                            Edit
                                                        </button>
                                                        <button
                                                            className="btn btn-link btn-sm text-danger text-decoration-none px-2"
                                                            onClick={() => removeEducation(idx)}
                                                        >
                                                            <i className="fa-regular fa-trash-can me-2"></i>
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </li>
                                ))}
                            {newEducation.map((e, idx) => (
                                <li key={"li_newEducation_" + idx} className="list-group-item">
                                    <EducationRow rowIsEditing={true} key={"li_newEducation_row" + idx} educationRow={e} idx={idx} />
                                    {editable && isEditingEducation && (
                                        <>
                                            <div className="col text-end">
                                                <button className="btn btn-primary px-4" onClick={saveNewEducation} disabled={dateEducationValidationError}>
                                                    Save
                                                </button>
                                                <button className="btn btn-danger ms-2" onClick={cancelNewEducation}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>
                    }
                    {editable && !isEditingEducation && !isSomePartBeingEdited && (
                        <div className="row">
                            <div className="col mt-3">
                                <button data-testid="education_add" className="w-100 btn btn-outline-info" onClick={addEducation}>
                                    Add education
                                </button>
                            </div>
                        </div>
                    )}
                    {editable && (
                        <div className="row">
                            <div className="col mt-3">
                                {spinnerEducationState === "working" ? (
                                    <div className="text-center fs-2 text-primary">
                                        <Spinner spinnerColor="text-primary" />
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    };

    const [isEditingContactInfo, setIsEditingContactInfo] = useState(false);

    const ContactInfo = () => {
        const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
        const editableOnClickHandler = (e: any) => {
            if (!editable) return;
            if (isSomePartBeingEdited) return;
            e.preventDefault();
            e.stopPropagation();
            setIsEditingContactInfo(true);
            setIsSomePartBeingEdited(true);
        };

        const saveContactCallback = async (linkedinValue: string | undefined, phoneValue: string | undefined) => {
            let isPhoneVerified = userInfo.verifications.phone;

            if (isPhoneVerified && userInfo?.other?.phone !== phoneValue) isPhoneVerified = false;

            await updateTechAPIAndLocalStorage(
                {
                    ...userInfo,
                    other: {
                        ...userInfo.other,
                        linkedin: linkedinValue,
                        phone: phoneValue,
                    },
                    verifications: {
                        ...userInfo.verifications,
                        phone: isPhoneVerified,
                    },
                },
                undefined,
            );

            setUserInfo({
                ...userInfo,
                other: {
                    ...userInfo.other,
                    linkedin: linkedinValue,
                    phone: phoneValue,
                },
                verifications: {
                    ...userInfo.verifications,
                    phone: isPhoneVerified,
                },
            });
            setIsEditingContactInfo(false);
            setIsSomePartBeingEdited(false);
            setCurrWorkingState("done");
        };

        const cancelEditContactHandler = async () => {
            setIsEditingContactInfo(false);
            setIsSomePartBeingEdited(false);
        };

        return (
            <div className="">
                {!isEditingContactInfo && (
                    <>
                        <div className={"title-section-alignment " + (workingState !== "done" || currWorkingState !== "done" ? "cssLoading " : "")}>
                            {userInfo.inmycontactlist && <DentalTechInfoContactReadonly userInfo={userInfo} currWorkingState={currWorkingState} />}
                            {editable && (
                                <DentalTechInfoContactEditable
                                    userInfo={userInfo}
                                    editableOnClickHandler={editableOnClickHandler}
                                    isSomePartBeingEdited={isSomePartBeingEdited}
                                />
                            )}

                            {!userInfo.inmycontactlist && userId && !userInfo.invitationsenttome && !userInfo.invitationsentstate && (
                                <>
                                    <div
                                        className="alert d-flex align-items-center bg-white fw-bold rounded-3 shadow-sm p-3 w-100"
                                        role="alert"
                                        data-testid="send-invitation-test"
                                    >
                                        <i className="fa-solid fa-user-plus fa-lg text-primary me-3"></i>
                                        <span>
                                            Send an invitation to{" "}
                                            <strong className={"fw-bold" + (!!userInfo.incognito ? " lab-can-not-see" : "")}>{userInfo.name}</strong> to see
                                            their contact information.
                                        </span>
                                    </div>
                                </>
                            )}
                            {!userInfo.inmycontactlist && userId && userInfo.invitationsentstate === "unread" && (
                                <>
                                    <div
                                        className="alert d-flex align-items-center bg-white rounded-3 shadow-sm p-3 w-100"
                                        role="alert"
                                        data-testid="waiting-for-reply-test"
                                    >
                                        <i className="fa-regular fa-hourglass-half fa-lg text-primary me-3"></i>
                                        <span className="fst-italic">
                                            Waiting for <strong className={"" + (!!userInfo.incognito ? " lab-can-not-see" : "")}>{userInfo.name}</strong>
                                            's reply. Hold on tight!
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}
                {isEditingContactInfo && (
                    <DentalTechInfoContactEdit
                        saveContactCallback={saveContactCallback}
                        cancelEditContactCallback={cancelEditContactHandler}
                        currWorkingState={currWorkingState}
                        setCurrWorkingState={setCurrWorkingState}
                        userInfo={userInfo}
                        workingState={workingState}
                    />
                )}
            </div>
        );
    };

    const Resume = () => {
        const refResume = useRef<HTMLDivElement>(null);

        const deleteResume = async () => {
            setWorkingState("working");
            await deleteResumeApi(userInfo.id);
            const updatedUserInfo = {
                ...userInfo,
                images: {
                    ...userInfo.images,
                    resume: "",
                },
            };
            await updateTechAPIAndLocalStorage(updatedUserInfo, undefined);
            setUserInfo(updatedUserInfo);
            setIsResumeViewerOpen(false);
            setResumeViewerUrl("");
            setSuccessUpload(false);
            setResumeUploadError("");
            setWorkingState("done");
        };
        useEffect(() => {
            if (userInfo && userInfo.images && userInfo.images.resume) {
                setSuccessUpload(true);
            }
            makeDroppableCapable(refResume, "resume", "resume", "fixed", (newfile, _inappropriateContent, _unsupportedImageType, errorMessage) => {
                updateResume(newfile, errorMessage);
                refreshOnboardingProgress();
            });
        }, [userInfo, userId, editable, updateResume]);

        return (
            <>
                {editable && (
                    <div className="p-0">
                        <h5 className="fw-bold mb-4">
                            <i className="fa-solid fa-file me-3 text-info"></i>
                            RESUME
                        </h5>
                        {(workingState !== "done" || state === "working") && (
                            <div className="text-center">
                                <Spinner spinnerColor="text-info fa-xl" />
                            </div>
                        )}
                        {!!successUpload && workingState === "done" && state !== "working" && (
                            <div className="row">
                                <div className="col-9 col-md-10">
                                    <button type="button" onClick={openResumeViewer} className="btn btn-info w-100 text-white">
                                        See Resume
                                        <i className="ms-2 fa-solid fa-arrow-up-right-from-square fa-2xs"></i>
                                    </button>
                                </div>
                                {!isSomePartBeingEdited && (
                                    <div className="col-3 col-md-2 d-flex justify-content-end">
                                        <div
                                            className="d-flex justify-content-center align-items-center bg-danger rounded-circle"
                                            style={{
                                                height: "30px",
                                                width: "30px",
                                            }}
                                            onClick={() => deleteResume()}
                                            role="button"
                                        >
                                            <i className="fa-regular fa-trash-can fa-xs text-white" data-testid="delete-icon"></i>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {!!!successUpload && workingState !== "working" && state !== "working" && (
                            <>
                                <div
                                    ref={refResume}
                                    style={{
                                        pointerEvents: !isSomePartBeingEdited ? "auto" : "none",
                                        opacity: !isSomePartBeingEdited ? 1 : 0.5,
                                    }}
                                    className="rounded p-2 border-info shadow-sm dashed-border text-center d-flex justify-content-center flex-column align-items-center"
                                >
                                    <p>
                                        <small>PDF or Word type files, max 10MB.</small>
                                    </p>
                                    <p>Drag and drop your resume onto this area or</p>
                                    <FileUploaderComponent
                                        relatedComponent={refResume}
                                        tooltipPosition="down"
                                        fileToolTipText=""
                                        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.doc,.docx"
                                    />
                                </div>
                                {!!resumeUploadError && (
                                    <ErrorMessageSticky
                                        message={resumeUploadError}
                                        closeClickHandler={() => {
                                            setResumeUploadError("");
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </>
        );
    };

    const [isEditingTechStatement, setIsEditingTechStatement] = useState(false);
    const TechStatement = () => {
        const [showError, setShowError] = useState(false);
        const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
        const [statementCharacterCount, setStatementCharacterCount] = useState<number>(0);

        const editableOnClickHandler = (e: any) => {
            if (!editable) return;
            if (isSomePartBeingEdited) return;
            e.preventDefault();
            e.stopPropagation();
            setIsEditingTechStatement(true);
            setIsSomePartBeingEdited(true);
        };

        const saveStatement = async () => {
            setCurrWorkingState("working");
            setShowError(false);
            try {
                await updateTechAPIAndLocalStorage(
                    {
                        ...userInfo,
                        other: {
                            ...userInfo.other,
                            description: refStatement.current.value,
                        },
                    },
                    undefined,
                );

                setUserInfo({
                    ...userInfo,
                    other: {
                        ...userInfo.other,
                        description: refStatement.current.value,
                    },
                });
                setIsEditingTechStatement(false);
                setIsSomePartBeingEdited(false);
            } catch (error: unknown) {
                if (ERROR_IS_FRAUD(error)) {
                    setShowError(true);
                }
                setIsEditingTechStatement(true);
                setIsSomePartBeingEdited(true);
            }

            setCurrWorkingState("done");
        };

        const cancelHandler = async () => {
            setShowError(false);
            setIsEditingTechStatement(false);
            setIsSomePartBeingEdited(false);
        };
        const refStatement = useRef<any>({});

        useEffect(() => {
            refStatement.current.value = userInfo.other.description || "";
            setStatementCharacterCount(refStatement.current.value.length);
        }, []);
        return (
            <>
                {!isEditingTechStatement && (
                    <>
                        <div className="title-section-alignment">
                            <h5 className="fw-bold m-0">
                                <i className="fa-solid fa-align-left me-3 text-info"></i>
                                ABOUT ME {isCandidate && " *"}
                            </h5>
                            {editable && !isSomePartBeingEdited && (
                                <div
                                    className="fileTooltip rightEdit ms-auto"
                                    role="button"
                                    data-tooltip=" Edit "
                                    onClick={editableOnClickHandler}
                                    data-testid="edit-icon"
                                >
                                    <i className="fa-solid fa-pen-to-square fa-xs p-2 shadow editIcon" data-testid="edit-icon"></i>
                                </div>
                            )}
                        </div>
                        <div
                            className={
                                "mt-3 overflow-auto aboutTech text-break " +
                                (workingState !== "done" || currWorkingState !== "done" ? " cssLoading " : "") +
                                (!!userInfo.incognito && !editable ? " lab-can-not-see" : "")
                            }
                        >
                            <p>{userInfo?.other?.description}</p>
                            {!!!userInfo.other.description && (
                                <p style={{ color: "#a5a5a5" }} className="m-0">
                                    <small> No information provided yet.</small>
                                </p>
                            )}
                        </div>
                    </>
                )}
                {isEditingTechStatement && (
                    <>
                        {showError && (
                            <ErrorMessageFraud
                                closeClickHandler={() => {
                                    setShowError(false);
                                }}
                            />
                        )}
                        <div>
                            <h6>
                                {" "}
                                <i className="fa-solid fa-align-left me-2"></i>
                                About Me:
                            </h6>
                            <p>
                                <small className="font-weight-light">
                                    Please provide a compelling statement of how your skills and contributions can positively impact the company.
                                </small>
                                <textarea
                                    disabled={currWorkingState === "working"}
                                    className="form-control text-justify mt-2"
                                    ref={refStatement}
                                    rows={6}
                                    defaultValue={userInfo?.other?.description || ""}
                                    maxLength={LARGE_TEXT_INPUT_MAX_CHARACTERS}
                                    onChange={(e) => setStatementCharacterCount(e.target.value.length)}
                                />
                                <small className="text-muted d-block mt-2">
                                    {statementCharacterCount}/{LARGE_TEXT_INPUT_MAX_CHARACTERS} characters
                                </small>
                            </p>
                        </div>
                        <div className="d-grid gap-3 mt-2 w-100 d-md-block">
                            {currWorkingState !== "working" && (
                                <button className="btn btn-primary px-4 " onClick={saveStatement}>
                                    {" "}
                                    Save{" "}
                                </button>
                            )}
                            {currWorkingState === "working" && (
                                <span className="text-primary fw-semibold">
                                    Just making sure your text is spam-free... {""} <Spinner spinnerColor="text-primary" />{" "}
                                </span>
                            )}
                            <button disabled={currWorkingState === "working"} className="btn btn-danger ms-md-2 px-3" onClick={cancelHandler}>
                                Cancel
                            </button>
                        </div>
                    </>
                )}
            </>
        );
    };

    const IncognitoHandler = async () => {
        setWorkingState("working");
        const newIncognitoState = !userInfo.incognito;

        await incognitoModeApi(userInfo.id, newIncognitoState);

        const updatedUserInfo = {
            ...userInfo,
            incognito: newIncognitoState,
        };

        await updateTechAPIAndLocalStorage(updatedUserInfo);
        setUserInfo(updatedUserInfo);
        setWorkingState("done");
    };

    const [isEditingIdealJob, setIsEditingIdealJob] = useState(false);
    const IdealJobSection = () => {
        const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
        const [idealJobCharacterCount, setIdealJobCharacterCount] = useState<number>(0);

        const editableOnClickHandler = (e: any) => {
            if (!editable) return;
            if (isSomePartBeingEdited) return;
            e.preventDefault();
            e.stopPropagation();
            setIsEditingIdealJob(true);
            setIsSomePartBeingEdited(true);
        };

        const saveIdealJobHandler = async () => {
            setCurrWorkingState("working");
            await updateTechAPIAndLocalStorage(
                {
                    ...userInfo,
                    other: {
                        ...userInfo.other,
                        idealJob: refIdealJob.current.value,
                    },
                },
                undefined,
            );

            setUserInfo({
                ...userInfo,
                other: {
                    ...userInfo.other,
                    idealJob: refIdealJob.current.value,
                },
            });
            setIsEditingIdealJob(false);
            setIsSomePartBeingEdited(false);
            setCurrWorkingState("done");
        };

        const cancelHandler = async () => {
            setIsEditingIdealJob(false);
            setIsSomePartBeingEdited(false);
        };
        const refIdealJob = useRef<any>({});

        useEffect(() => {
            refIdealJob.current.value = userInfo.other.idealJob || "";
            setIdealJobCharacterCount(refIdealJob.current.value.length);
        }, []);
        return (
            <>
                {!isEditingIdealJob && (
                    <>
                        <div className="title-section-alignment">
                            <h5 className="fw-bold m-0">
                                <i className="fa-solid fa-bullseye me-3 text-info"></i>
                                MY IDEAL JOB
                            </h5>
                            {editable && !isSomePartBeingEdited && (
                                <div
                                    className="fileTooltip rightEdit ms-auto"
                                    role="button"
                                    data-tooltip=" Edit "
                                    onClick={editableOnClickHandler}
                                    data-testid="edit-icon"
                                >
                                    <i className="fa-solid fa-pen-to-square fa-xs p-2 shadow editIcon" data-testid="edit-icon"></i>
                                </div>
                            )}
                        </div>
                        <div
                            className={
                                "mt-3 overflow-auto aboutTech text-break " + (workingState !== "done" || currWorkingState !== "done" ? " cssLoading " : "")
                            }
                        >
                            <p>{userInfo?.other?.idealJob}</p>
                            {!!!userInfo.other.idealJob && (
                                <p style={{ color: "#a5a5a5" }} className="m-0">
                                    <small> No information provided yet.</small>
                                </p>
                            )}
                        </div>
                    </>
                )}
                {isEditingIdealJob && (
                    <>
                        <div>
                            <h6>
                                {" "}
                                <i className="fa-solid fa-bullseye me-2"></i>
                                My Ideal Job:
                            </h6>
                            <p>
                                <small className="font-weight-light">Describe your ideal job to attract the right opportunities.</small>

                                <textarea
                                    disabled={currWorkingState === "working"}
                                    className="form-control text-justify mt-2"
                                    ref={refIdealJob}
                                    rows={5}
                                    defaultValue={userInfo?.other?.idealJob || ""}
                                    maxLength={MEDIUM_TEXT_INPUT_MAX_CHARACTERS}
                                    onChange={(e) => setIdealJobCharacterCount(e.target.value.length)}
                                />
                                <small className="text-muted d-block mt-2">
                                    {idealJobCharacterCount}/{MEDIUM_TEXT_INPUT_MAX_CHARACTERS} characters
                                </small>
                            </p>
                        </div>
                        <div className="d-grid gap-3 mt-2 w-100 d-md-block">
                            <button
                                className="btn px-4 btn-primary"
                                onClick={saveIdealJobHandler}
                                disabled={workingState === "working" || currWorkingState === "working"}
                            >
                                {workingState === "working" || currWorkingState === "working" ? <Spinner /> : "Save"}
                            </button>

                            <button disabled={currWorkingState === "working"} className="btn btn-danger ms-md-2 px-3" onClick={cancelHandler}>
                                Cancel
                            </button>
                        </div>
                    </>
                )}
            </>
        );
    };

    return (
        <>
            {isResumeViewerOpen && !!resumeViewerUrl && <ResumeViewer onClose={closeResumeViewer} title={resumeViewerTitle} viewerUrl={resumeViewerUrl} />}
            {!userExist && <UserNotExist />}
            {userExist && (
                <>
                    <span id="upload_background-progress"></span>
                    <div
                        className={"container-fluid labprofilecontentblock " + (workingState === "done" ? "" : "cssLoading cssLoading_linearBgLoading ")}
                        ref={refBackgroundPhoto}
                        style={{
                            backgroundImage: backgroundImg && (!!!userInfo.incognito || editable) ? `url(${backgroundImg})` : " ",
                            height: "20rem",
                            position: "relative",
                        }}
                    >
                        <div className="container">
                            <div className="row pt-3">
                                <div className="d-flex justify-content-end align-items-center gap-3">
                                    {editable && <IncognitoButton workingState={workingState} onClickHandler={IncognitoHandler} enabled={userInfo.incognito} />}
                                    {editable && !isSomePartBeingEdited && (
                                        <FileUploaderComponent
                                            relatedComponent={refBackgroundPhoto}
                                            tooltipPosition="right"
                                            fileToolTipText="Click here to edit your background photo, or drag and drop it onto this area. Images should be in .JPG, .JPEG, .PNG, .HEIC or .HEIF formats. Your background image should be 1220x320 pixels. "
                                        />
                                    )}
                                </div>
                                {!editable && <BackButton />}
                                {!document.querySelector(".cssLoading") && (uploadImageError || inappropriateContent || unsupportedImageType) && (
                                    <ErrorMessageSticky
                                        closeClickHandler={() => {
                                            setUploadImageError("");
                                            setInappropriateContent(false);
                                            setUnsupportedImageType(false);
                                        }}
                                        message={
                                            uploadImageError
                                                ? uploadImageError
                                                : inappropriateContent
                                                  ? "Image not accepted. It may contain inappropriate content. Please try again with a suitable image."
                                                  : unsupportedImageType
                                                    ? "Image format not accepted. Please use JPEG, JPG, PNG, HEIC, or HEIF formats."
                                                    : ""
                                        }
                                    />
                                )}
                            </div>
                            {!!nextOnboardingStep && editable && (
                                <OnboardingProcess progress={onboardingStatus} nextStep={nextOnboardingStep} remainingSteps={remainingSteps} userType="tech" />
                            )}
                        </div>
                    </div>
                    <div className="container-fluid">
                        <div className="container">
                            <div className="row gap-3 justify-content-center lab-row">
                                <div className={"col-lg-5 rounded p-4 " + (!!userInfo.incognito ? "incognito-active" : "bg-light")}>
                                    <div className="row">
                                        <span id="upload_profile_photo-progress"></span>
                                        {/* col-lg-4 is inside LogoTech component */}
                                        {editable && (
                                            <LogoTech
                                                editable={editable}
                                                isSomePartBeingEdited={isSomePartBeingEdited}
                                                setInappropriateContent={setInappropriateContent}
                                                setUnsupportedImageType={setUnsupportedImageType}
                                                setUploadErrorMessage={setUploadImageError}
                                                userInfo={userInfo}
                                                workingState={workingState}
                                                extraClass="border-light profile-logo profile-logo-editable"
                                                onboardingStepCompleted={refreshOnboardingProgress}
                                            />
                                        )}
                                        {!editable && (
                                            <div className="col col-lg-4 text-center profile-logo-container">
                                                <LogoTech
                                                    editable={false}
                                                    isSomePartBeingEdited={false}
                                                    setInappropriateContent={() => {}}
                                                    setUnsupportedImageType={() => {}}
                                                    userInfo={userInfo}
                                                    workingState={workingState}
                                                    extraClass="border-light profile-logo"
                                                    onboardingStepCompleted={refreshOnboardingProgress}
                                                />
                                            </div>
                                        )}
                                        <div className="col-lg-8">
                                            <NameSection />
                                            <span id="fill_contact_info-progress"></span>
                                            <ContactInfo />
                                            {currentUserInfo?.type === "lab" && (
                                                <>
                                                    {!userInfo.inmycontactlist && <InviteButtons />}
                                                    {userInfo.inmycontactlist && (
                                                        <MessageBtn userInfo={userInfo} className="btn-outline-secondary w-100 rounded-3 mt-4 mb-2" />
                                                    )}
                                                </>
                                            )}
                                            {currentUserInfo?.type === "lab" && (
                                                <>
                                                    {userInfo &&
                                                        userInfo.images &&
                                                        userInfo.images.resume &&
                                                        userInfo.inmycontactlist &&
                                                        !!!userInfo.incognito && (
                                                            <div className="mb-2">
                                                                <button
                                                                    className="btn btn-outline-info w-100 rounded-3"
                                                                    type="button"
                                                                    onClick={openResumeViewer}
                                                                >
                                                                    <i className="fa-regular fa-file me-2"></i>
                                                                    View Resume
                                                                    <i className="fa-solid fa-arrow-up-right-from-square fa-2xs ms-2"></i>
                                                                </button>
                                                            </div>
                                                        )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className={"col-lg-6 rounded p-4 " + (!!userInfo.incognito ? " incognito-active" : "bg-light")}>
                                    <span id="fill_about_me-progress"></span>
                                    <TechStatement />
                                </div>
                            </div>
                        </div>

                        <div className="container mt-4">
                            <div className="row gap-3 justify-content-center lab-row">
                                <div className="col-lg-5 ">
                                    <div className="row bg-light rounded p-4 mb-4">
                                        <PositionsSection />
                                    </div>
                                    <span id="fill_personal_profile_info-progress"></span>
                                    <div className="row bg-light rounded p-4 mb-4">
                                        <TechInfoSection />
                                    </div>
                                    <span id="fill_ideal_job-progress"></span>
                                    <div className="row bg-light rounded p-4 mb-4">
                                        <IdealJobSection />
                                    </div>
                                    {isCandidate && (
                                        <>
                                            <div className={"row rounded p-4 mb-4 " + (!!userInfo.incognito ? " incognito-active" : "bg-light")}>
                                                <Resume />
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="col-lg-6 ">
                                    <span id="portfolio_gallery-progress"></span>
                                    <div className={"row rounded p-4 mb-4 " + (!!userInfo.incognito ? "incognito-active" : " bg-light ")}>
                                        <WorkEvidence
                                            editable={editable}
                                            isSomePartBeingEdited={isSomePartBeingEdited}
                                            setIsSomePartBeingEdited={setIsSomePartBeingEdited}
                                            setUploadErrorMessage={setUploadImageError}
                                            userId={Number(userId) || (currentUserInfo?.id as number)}
                                            currentUserInfo={currentUserInfo}
                                            onboardingStepCompleted={refreshOnboardingProgress}
                                            isIncognito={userInfo.incognito}
                                        />
                                    </div>
                                    <span id="fill_experience-progress"></span>
                                    <div className={"row rounded p-4 mb-4 " + (!!userInfo.incognito ? "incognito-active" : " bg-light ")}>
                                        <ExperienceSection />
                                    </div>
                                    <span id="fill_education-progress"></span>
                                    <div className={"row rounded p-4 mb-4 " + (!!userInfo.incognito ? "incognito-active" : " bg-light ")}>
                                        <EducationSection />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {isSmallDevice.fulfilled && isOwnProfile && (
                        <ProfileDock progress={onboardingStatus} nextStep={nextOnboardingStep ?? null} userType={currentUserInfo?.type ?? "tech"} />
                    )}
                </>
            )}
        </>
    );
};

export const DentalTechInfo = ({ editable = false }: Pick<TDentalTechProps, "editable">) => {
    const { userId } = useParams();
    const { getTech, state } = useTechnicians();
    const [userInfo, setUserInfo] = useState<TApiTechnician[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [techName, setTechName] = useState("");

    useEffect(() => {
        const isFirstLogin = localStorage.getItem("firstLogin");
        const savedName = localStorage.getItem("techName");
        const savedLastName = localStorage.getItem("techLastName");
        const fullName = `${savedName} ${savedLastName}`;
        if (isFirstLogin) {
            setShowModal(true);
            setTechName(fullName || "");
            localStorage.removeItem("firstLogin");
            localStorage.removeItem("techName");
            localStorage.removeItem("techLastName");
        }
    }, []);

    useEffect(() => {
        if (userId) {
            getTech(Number(userId)).then((userInfo) => {
                setUserInfo(userInfo);
            });
        }
    }, [userId]);

    return (
        <>
            <NotificationProvider>
                <Header title="" />
                <>
                    {showModal && <WelcomeModal techName={techName} />}
                    {state === "working" && (
                        <div className="text-center">
                            <Spinner spinnerColor="text-primary fa-xl" />
                        </div>
                    )}
                    {state !== "working" && (
                        <>
                            <TechInfo editable={editable} userExist={userId === undefined ? true : userInfo.length > 0} techInfo={userInfo[0]} />
                            {userInfo.length > 0 && (
                                <div className="container-fluid">
                                    <div className="container">
                                        <div className="row justify-content-center justify-content-lg-end lab-row mt-5">
                                            <div className="w-auto py-3 px-md-5 text-center text-md-end">
                                                <ReportButton userId={Number(userId || "")} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
                <Footer />
            </NotificationProvider>
        </>
    );
};
